import { ArrowLeft, ArrowUpRight, CircleCheck, Sparkles } from 'lucide-react';
import { useEffect, type CSSProperties } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router';
import { useAuth } from '../../app/auth';
import { customRole, Routes } from '../../app/routes';
import { useCatalog, useEmployees, useWorkspaces } from '../../data/queries';
import type { EmployeeModule, Workspace } from '../../domain/models';
import { canRequestEmployee } from '../../domain/models';
import { useT } from '../../l10n/i18n';
import { EmployeeAvatar } from '../core/widgets/EmployeeAvatar';
import { ErrorView, Skeleton } from '../core/widgets/common';
import { byCategory, categoryName, worksIn } from './hire';
import { teamHref } from './team';
import './hire.css';

// The shelf of employees Talkos offers (ADR 0010), by category: the shop window. Choosing one opens
// its hiring form; someone not on the shelf can be described in words. `?workspace=` carries the
// business the person came from.

export function HirePage() {
  const t = useT();
  const { session } = useAuth();
  const [params] = useSearchParams();
  const workspace = params.get('workspace') ?? undefined;
  const catalogQ = useCatalog();
  const employees = useEmployees().data ?? [];
  const workspaces = useWorkspaces().data ?? [];
  // The router keeps the scroll of the page the person came from; the shelf starts at its top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!canRequestEmployee(session)) return <Navigate to={teamHref(workspace)} replace />;

  const modules = catalogQ.data;
  const groups = modules && byCategory(modules);
  const jump = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  let body;
  if (!modules && catalogQ.isError) {
    body = <ErrorView error={catalogQ.error} onRetry={() => void catalogQ.refetch()} />;
  } else if (!groups) {
    body = (
      <ul className="hire-grid" aria-busy>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <li key={i}>
            <Skeleton height={232} radius={22} />
          </li>
        ))}
      </ul>
    );
  } else {
    body = (
      <>
        {groups.map((g) => (
          <section key={g.category} id={`hire-${g.category}`} className="hire-category" aria-labelledby={`hire-${g.category}-title`}>
            <h2 id={`hire-${g.category}-title`} className="hire-category-title">
              {categoryName(t, g.category)}
              <span className="hire-category-count" aria-hidden>
                {g.modules.length}
              </span>
            </h2>
            <ul className="hire-grid">
              {g.modules.map((m) => (
                <li key={m.role}>
                  <ModuleCard module={m} works={worksIn(m.role, employees, workspaces)} workspace={workspace} />
                </li>
              ))}
            </ul>
          </section>
        ))}
        <section id="hire-custom" className="hire-category" aria-label={t('hireCustom')}>
          <Link to={Routes.hireModule(customRole, workspace)} className="hire-custom">
            <span className="hire-custom-icon" aria-hidden>
              <Sparkles size={26} />
            </span>
            <span className="hire-custom-text">
              <span className="title-large">{t('hireCustom')}</span>
              <span className="body-medium muted">{t('hireCustomBody')}</span>
            </span>
            <ArrowUpRight size={22} className="hire-card-go" aria-hidden />
          </Link>
        </section>
      </>
    );
  }

  return (
    <div className="page page-wide hire">
      <Link to={teamHref(workspace)} className="btn btn-text btn-small hire-back">
        <ArrowLeft size={18} aria-hidden />
        {t('navTeam')}
      </Link>
      <header className="hire-hero">
        {modules && modules.length > 0 && (
          <div className="hire-hero-faces" aria-hidden>
            {modules.slice(0, 7).map((m, i) => (
              <span key={m.role} style={{ '--i': i } as CSSProperties}>
                <EmployeeAvatar avatar={m.avatar} color={m.color} size={56} animate={i === 3} />
              </span>
            ))}
          </div>
        )}
        <h1 className="hire-title">{t('hireTitle')}</h1>
        <p className="body-large muted hire-lead">{t('hireLead')}</p>
        {groups && (
          <nav className="hire-jump" aria-label={t('hireTitle')}>
            {groups.map((g) => (
              <button key={g.category} type="button" className="hire-jump-chip" onClick={() => jump(`hire-${g.category}`)}>
                {categoryName(t, g.category)}
              </button>
            ))}
            <button type="button" className="hire-jump-chip" onClick={() => jump('hire-custom')}>
              {t('hireCustom')}
            </button>
          </nav>
        )}
      </header>
      {body}
    </div>
  );
}

/** One employee on the shelf: face, name, what they do in a line, and where they already work. */
function ModuleCard({ module: m, works, workspace }: { module: EmployeeModule; works: Workspace[]; workspace?: string }) {
  const t = useT();
  return (
    <Link to={Routes.hireModule(m.role, workspace)} className="hire-card" style={{ '--emp': m.color } as CSSProperties}>
      <span className="hire-card-top">
        <span className="hire-card-face">
          <EmployeeAvatar avatar={m.avatar} color={m.color} size={64} />
        </span>
        <ArrowUpRight size={22} className="hire-card-go" aria-hidden />
      </span>
      <span className="hire-card-body">
        <span className="title-large">{m.name}</span>
        <span className="label-medium hire-card-role">{m.title}</span>
        <span className="body-medium muted hire-card-summary">{m.summary}</span>
      </span>
      {works.length > 0 && (
        <span className="label-small hire-card-works">
          <CircleCheck size={14} strokeWidth={2.4} aria-hidden />
          <span>{t('hireAlreadyWorks', { workspaces: works.map((w) => w.name).join(', ') })}</span>
        </span>
      )}
    </Link>
  );
}
